import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOMeta from '@/components/base/SEOMeta';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';

const websiteUrl = 'https://leyusports.com';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find(a => a.id === id);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center py-32">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-article-line text-foreground-400 text-3xl"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">您要查看的文章可能已被删除或不存在</p>
            <Link to="/" className="inline-block px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter(a => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '乐鱼体育资讯中心',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${websiteUrl}/article/${article.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: websiteUrl },
      { '@type': 'ListItem', position: 2, name: article.category, item: `${websiteUrl}/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  return (
    <>
      <SEOMeta
        title={`${article.title} - 乐鱼体育资讯中心`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`${websiteUrl}/article/${article.id}`}
        ogType="article"
        ogImage={article.imageUrl}
        schemaJson={{ '@context': 'https://schema.org', '@graph': [articleSchema, breadcrumbSchema] }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <article className="max-w-[860px] mx-auto px-4 md:px-6 pb-16">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.category, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full hover:bg-primary-200 transition-colors cursor-pointer"
              >
                {article.category}
              </Link>
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full">
                  {tag}
                </span>
              ))}
            </div>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight mb-6">
              {article.title}
            </h1>

            <div className="flex items-center gap-4 text-sm text-foreground-500 pb-6 border-b border-background-200">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-full">
                  <i className="ri-user-line text-primary-600 text-sm"></i>
                </div>
                <span className="font-medium text-foreground-700">{article.author}</span>
              </div>
              <span className="text-foreground-300">|</span>
              <div className="flex items-center gap-1">
                <i className="ri-calendar-line text-sm"></i>
                <span>{article.publishDate}</span>
              </div>
              <span className="text-foreground-300">|</span>
              <div className="flex items-center gap-1">
                <i className="ri-time-line text-sm"></i>
                <span>{article.readTime}阅读</span>
              </div>
            </div>
          </div>

          <div className="mb-10 rounded-xl overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              className="w-full h-[400px] md:h-[500px] object-cover"
            />
          </div>

          <div
            className="prose-custom text-foreground-800 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="mt-12 pt-8 border-t border-background-200">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <span className="text-sm text-foreground-500">分享到：</span>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 transition-colors cursor-pointer text-foreground-500 hover:text-primary-600">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 transition-colors cursor-pointer text-foreground-500 hover:text-primary-600">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 transition-colors cursor-pointer text-foreground-500 hover:text-primary-600">
                  <i className="ri-link"></i>
                </button>
              </div>
              <Link
                to={`/category/${article.categorySlug}`}
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer"
              >
                查看更多{article.category}资讯 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-16">
            <div className="max-w-[860px] mx-auto px-4 md:px-6">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">相关推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="w-full h-40 overflow-hidden">
                      <img src={ra.imageUrl} alt={ra.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                    <div className="p-4">
                      <span className="text-xs text-accent-600 font-medium">{ra.category}</span>
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 mt-1 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-400 mt-2">{ra.publishDate}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}